/**
 * `processPlayerInput` — the orchestrating entry point (architecture.md §3.3,
 * §7.2): catalog → parse → outcome, with EVERY arm returning a fully
 * committed next state (D23).
 *
 * @remarks
 * D23's commit-everything ruling (R6, 2026-08-13): `(bundle, seed, raw
 * inputs)` folded through THIS function alone must reproduce the complete
 * session — transcript, history, and world — byte for byte. So every arm
 * commits: `commandHistory` gains the raw input VERBATIM (blank and bizarre
 * inputs are real inputs — D09), the transcript gains a `player`-source echo
 * for every non-blank input, and non-executed arms stamp their response
 * lines (rejection narratives, parse teaching) into the transcript under
 * the `input-<n>-*` id namespace — disjoint from `turn-<n>-*` and
 * `session-start-*`, and deterministic because `<n>` is the input's ordinal
 * in `commandHistory`. Consumers adopt `nextState` wholesale and switch on
 * the arm only for UI extras (park a clarification, focus an input); they
 * never append transcript themselves (§8, amended by D23).
 *
 * Multi-clause policy (D23, amended by R21): clauses execute left to right,
 * one consumed turn each; the first refusal stops the walk WITH the prefix
 * committed, and the arm reports how the input ENDED. The parser's
 * `parsedPrefix` on clarifications and failures runs the same way (§2.1 left
 * the choice to the consumer: we run the prefix). R21's law: each clause
 * reads the world its predecessors left — the whole-input parse contributes
 * segmentation and the FIRST clause's reading, and every later clause
 * re-parses its own text against the catalog derived from the walk's current
 * state (`walk-clauses.ts`), so mid-chain clarifications cite CURRENT
 * candidates and a chain behaves exactly as typing its clauses one input at
 * a time, minus the extra echoes.
 *
 * Clarification questions are conversational UI, not world events: they are
 * NOT stamped into the transcript (the player echo is; the question is
 * parked by the consumer). Turn consumption is unchanged from R5: executed
 * clauses advance `turnCount` inside `executeTurn`; refusals and failures
 * never do.
 *
 * Cross-input pronoun recency ("take the bar" … next input … "use it") is
 * deliberately absent in v0: it wants a recency field in `RuntimeState`,
 * which is a schema decision, not a slip — in-input chains ("take the
 * keycard then swipe it") work today via the parser's own threading.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import { type EntityRef, type ParseCandidateEntity, parseCommand } from '@orpheus/parser-engine';

import type { ActionRejection } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { dialogueAnswerOf } from '../conversation/prompts.js';
import { offeredAnswersSentence, type SpokenResponse } from '../defaults/default-responses.js';
import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { describeEntityRef } from '../entities/describe-entity-ref.js';
import { readAnswerAttempt } from './answer-attempt.js';
import { lastNamingOf, volunteerBareNounFollowUp } from './bare-noun.js';
import { readChrome } from './chrome-directives.js';
import { carryingSettlement, clauseCatalogOf, ruledOutByAnswer, type RuledOutRefs } from './clause-settlement.js';
import { speakChainAbandoned, speakLineOntoWalk } from './clause-walk.js';
import { pronounAntecedentsOf } from './conversational-partner.js';
import { withCorrectionQuestion } from './correction-question.js';
import { answerDialogueQuestion } from './dialogue-answer.js';
import { buildParseContext } from './parse-context.js';
import { finishParseFailure, finishWithReaction } from './parse-failure-arm.js';
import { questionForMissingObject, settleRefusedAnswer, withPendingCompletion } from './pending-completion.js';
import { applyQuestionSurvival, clearQuestion, parkQuestion, whichEntityAnswerRef } from './pending-question.js';
import { type PlayerInputOutcome } from './player-input-outcome.js';
import { recentRefsOf } from './pronoun-recency.js';
import { refuseBecauseStoryEnded } from './terminal-gate.js';
import { inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';
import { walkInputClauses } from './walk-clauses.js';
import { isWithdrawalDirective, withdrawParkedQuestion } from './withdraw-question.js';

// The outcome type lives beside the arms it describes; every consumer still reads it from here.
export { type PlayerInputOutcome } from './player-input-outcome.js';

/**
 * Process one raw player input end to end.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed
 * @returns One §3.3 outcome, next state committed on every arm
 */
export const runPlayerInput = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	parseText: string = rawInput,
	spokenTeachOverride?: SpokenResponse,
	// D247: the catalog the asking clause is read against once a which-one
	// answer has settled part of it (`clauseCatalogOf`) — absent, the world's.
	clauseCatalog?: readonly ParseCandidateEntity[],
	// §3.2 (D84, scoped by D91): an ANSWER's refusal may be re-voiced in the
	// question's terms — applied to the FIRST clause only, because a splice
	// puts the answer there and everything after it is the player's own chain,
	// whose refusals speak in their own voice.
	rejectionOverride?: (rejection: ActionRejection) => SpokenResponse | undefined,
): PlayerInputOutcome => {
	// The input's ordinal names its transcript id namespace — deterministic
	// because commandHistory only ever grows, one entry per input (D09/D23).
	const inputOrdinal = state.session.commandHistory.length + 1;
	const openingCursor = inputNamespaceCursor(inputOrdinal, 0, state);

	// Commit the verbatim history and the player echo before anything can fail.
	const echo =
		rawInput.trim() === ''
			? { cursor: openingCursor, entries: [] as readonly RuntimeTranscriptEntry[] }
			: stampNarrativeLines(bundle, state, openingCursor, [{ priority: 'standard', source: 'player', text: rawInput }], 'player_input');

	const openedState: RuntimeState = {
		...state,
		session: {
			commandHistory: [...state.session.commandHistory, rawInput],
			// D235: who the player is talking with outlives the input that said so.
			...(state.session.conversationPartnerId === undefined ? {} : { conversationPartnerId: state.session.conversationPartnerId }),
			recentEntityRefs: state.session.recentEntityRefs,
			responseUsage: state.session.responseUsage,
			transcript: [...state.session.transcript, ...echo.entries],
		},
	};

	// R11: the terminal gate — a closed world declines commands (the whole
	// reading lives in terminal-gate.ts). Checked AFTER the echo commit so
	// replays of post-ending inputs stay byte-exact like every other arm.
	const endedOutcome = refuseBecauseStoryEnded(bundle, openedState, inputOrdinal, echo.entries);

	if (endedOutcome !== undefined) {
		return endedOutcome;
	}

	// D37: what the player TYPED is echoed and recorded; what the engine
	// PARSES may be the spliced answer to a parked question. Replays stay
	// exact because history keeps the typed words and the splice re-derives.
	// D79: a resolved which-one answer re-parses its clause with the LOSING
	// candidates ruled out — the ambiguity vanishes and no text surgery
	// happens — and D247 keeps them ruled out for the answer to any question
	// that clause asks next. Absent a settlement, the full catalog stands.
	const partnerAntecedents = pronounAntecedentsOf(bundle, state);
	const parseOutcome = parseCommand(
		parseText,
		buildParseContext(bundle, clauseCatalog ?? deriveEntityCatalog(bundle, state), {
			// D67: cross-input pronoun recency. The parser has resolved "it" through
			// a recency view since Phase 1a; this is the engine finally FEEDING it —
			// the refs the last executed input referred to, carried in session state
			// so replays resolve pronouns byte-identically.
			recentEntityRefs: state.session.recentEntityRefs as unknown as readonly EntityRef[],
			// D235: and who the player is talking with — the partner's own pronoun names the partner ahead of that list.
			...(partnerAntecedents === undefined ? {} : { pronounAntecedents: partnerAntecedents }),
		}),
	);
	// R21: the parse-and-execute interleave — the whole-input parse above
	// contributed segmentation and the first clause's reading; every later
	// clause re-parses inside the walk against the state its predecessors
	// left. Corrections aggregate across every clause the parser actually
	// read, deterministic in clause order (R7).
	const walkResult = walkInputClauses(
		bundle,
		openedState,
		parseOutcome,
		inputOrdinal,
		echo.entries,
		state.session.recentEntityRefs as unknown as readonly EntityRef[],
		rejectionOverride,
	);

	// A refusal outranks whatever a later clause would have asked or taught:
	// the input already ended (and the walk spoke the §8.8 abandonment).
	if (walkResult.type === 'rejected') {
		return {
			corrections: walkResult.corrections,
			nextState: walkResult.walk.state,
			rejectedClauseText: walkResult.rejection.clauseText,
			rejection: walkResult.rejection.rejection,
			transcriptEntries: walkResult.walk.entries,
			type: 'action_rejected',
		};
	}

	// D226 (E6): a bare declared-unsupported verb the pack wrote a reaction
	// for — spoken through the reaction tail, exactly as a whole-input egg is.
	if (walkResult.type === 'answered_by_reaction') {
		return finishWithReaction(bundle, walkResult.walk, walkResult.reaction, walkResult.corrections, inputOrdinal, walkResult.remainderTexts);
	}

	if (walkResult.type === 'stopped_for_clarification') {
		/*
		 * R10: the ask PRINTS (the record needs its questions), and D81 (Calder's
		 * D2, ruled by Mike) orders it BEFORE the §8.8 coda — cause →
		 * consequence, exactly as a refusal already reads: the question stopped
		 * the chain, so the question speaks first and the abandonment is its
		 * tail. The chip surface owns adjacency to the input line; the
		 * transcript is the record. The park (applyQuestionSurvival) reads the
		 * spoken text off this stamp, the questionForMissingObject pattern.
		 * Since R21 the candidates are CURRENT by construction — a mid-chain
		 * ask cites the room the walk is standing in, never the input-start one.
		 */
		// D86 (the export contract's one engine ask): the ask's entry carries
		// the answers it OFFERED — display names, presented order, resolved
		// now, because the candidates leave the parked question the moment it
		// is answered and the record must not show a question with no options.
		// D229 (E14): the LINE names them too, as one sentence through the
		// slot's `{options}` — the chips named them and the line did not, and
		// a screen reader or the terminal hears only the line.
		const offeredAnswers = walkResult.clarification.candidates.map((candidate) => describeEntityRef(bundle, candidate.entityRef));
		const askedWalk = speakLineOntoWalk(
			bundle,
			walkResult.walk,
			inputOrdinal,
			'clarificationQuestion',
			`question_asked:${String(inputOrdinal)}`,
			{ options: offeredAnswersSentence(bundle, offeredAnswers) },
			{ offeredAnswers },
		);
		const stoppedWalk =
			walkResult.remainderTexts.length === 0
				? askedWalk
				: speakChainAbandoned(bundle, askedWalk, inputOrdinal, walkResult.remainderTexts);

		return {
			clarification: walkResult.clarification,
			corrections: walkResult.corrections,
			nextState: stoppedWalk.state,
			transcriptEntries: stoppedWalk.entries,
			type: 'clarification_required',
		};
	}

	if (walkResult.type === 'stopped_for_parse_failure') {
		// The egg/teach tail lives in parse-failure-arm.ts (the 500-cap split):
		// D27 reaction lookup, the D24/D38 teach, the R12 occurrence, and the
		// §8.8 abandonment coda. D91: the D38 answer-voice override speaks only
		// when the failure is the ANSWER's own — a splice puts the answer in
		// the first clause, and a refusal consumes no turn, so any turn
		// consumed before this stop means a LATER clause of the player's chain
		// failed and the normal teach speaks.
		const isAnswersOwnFailure = walkResult.walk.state.progression.turnCount === openedState.progression.turnCount;

		return finishParseFailure(
			bundle,
			walkResult.walk,
			walkResult.diagnostics,
			rawInput,
			walkResult.corrections,
			isAnswersOwnFailure ? spokenTeachOverride : undefined,
			inputOrdinal,
			walkResult.remainderTexts,
		);
	}

	// D67: an EXECUTED input becomes the pronoun antecedent for the next one —
	// its referenced things, most recent first. Only executed inputs update the
	// view: a refusal or a parse failure is not the player successfully talking
	// about a thing, and clobbering the antecedent on noise would break the
	// natural "take the bar" / (mash) / "use it" sequence.
	const spokenAboutRefs = recentRefsOf(walkResult.executedCommands);
	const rememberedState: RuntimeState =
		spokenAboutRefs.length === 0
			? walkResult.walk.state
			: {
				...walkResult.walk.state,
				session: { ...walkResult.walk.state.session, recentEntityRefs: spokenAboutRefs },
			};

	// D37 → D133: a bare noun nobody asked about describes itself, then asks
	// what the player wants done with it — the inventory becomes
	// conversational. The PARSER says which executed command was a name
	// (`rescuedAs`), so the offer follows every path through here: the plain
	// input, and the answer to a which-one question that re-ran a naming
	// clause with the losers removed (D123's open half).
	const lastNaming = lastNamingOf(walkResult.executedCommands);
	const offeredState = lastNaming === undefined ? rememberedState : volunteerBareNounFollowUp(bundle, rememberedState, lastNaming);

	return {
		corrections: walkResult.corrections,
		result: {
			firedEventIds: walkResult.walk.firedEventIds,
			nextState: offeredState,
			transcriptEntries: walkResult.walk.entries,
		},
		type: 'executed',
	};
};

/**
 * Process one raw player input end to end (architecture.md §3.3).
 *
 * D37 → D79: the pipeline is wrapped in the session's CONVERSATION. A parked
 * question (completion or which-one) is state the player can see, and it
 * SURVIVES the player doing other things — noise, refusals, executed commands
 * — until it is answered, a newer question replaces it (D66 generalized), or
 * the player leaves the room it was asked in, which closes it with one line
 * (`questionWithdrawn`). Which-one answers resolve among the offered
 * candidates and re-run the asking clause with the losers removed; completion
 * answers splice, exactly as D37 built them; an actor's question (D235) is
 * answered by the words it offered, and the answer is a turn of its own.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed (a chip submits its candidate's name here)
 * @returns One §3.3 outcome, next state committed on every arm
 */
export const processPlayerInput = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): PlayerInputOutcome =>
	processPlayerWords(bundle, state, rawInput, rawInput);

/**
 * The two questions a NAME answers (extracted at the complexity cap when D235
 * added the second): an actor's question, whose offered words are matched
 * whole and never fuzzily, and whose answer is a turn with no command in it
 * (`dialogue-answer.ts`); and the parser's which-one question, whose answer
 * re-runs the asking clause with the losing candidates removed. Words that
 * answer neither are the player's own command, read next with the question
 * still parked (D79's survival).
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed
 * @param words - The words being read as this input
 * @param parkedQuestion - The question open before this input, if any
 * @returns The outcome of the answer, or `undefined` when the words answered nothing by name
 */
const answerParkedQuestionByName = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	words: string,
	parkedQuestion: RuntimeState['session']['pendingQuestion'],
): PlayerInputOutcome | undefined => {
	if (parkedQuestion?.kind === 'dialogue') {
		const answer = dialogueAnswerOf(parkedQuestion, words);

		return answer === undefined ? undefined : answerDialogueQuestion(bundle, state, rawInput, parkedQuestion, answer.optionId);
	}

	if (parkedQuestion?.kind !== 'which_entity') {
		return undefined;
	}

	const chosenRef = whichEntityAnswerRef(bundle, state, words, parkedQuestion);

	if (chosenRef === undefined) {
		return undefined;
	}

	const ruledOutRefs = ruledOutByAnswer(parkedQuestion, chosenRef);
	const resolvedOutcome = runPlayerInput(
		bundle,
		clearQuestion(state),
		rawInput,
		parkedQuestion.clauseText,
		undefined,
		clauseCatalogOf(bundle, state, ruledOutRefs),
	);

	// The resolved clause may itself refuse-and-ask, or ask which about
	// another of its words; whatever it earned is the conversation now, and
	// (D247) it carries what this answer settled. The which-one question is
	// ANSWERED either way — the player chose — so it never re-parks.
	return carryingSettlement(applyQuestionSurvival(bundle, parkedQuestion, 'answer', withPendingCompletion(bundle, resolvedOutcome)), ruledOutRefs);
};

/**
 * D247: what a question asked by the ANSWER's own clause carries — the
 * settlement that clause was read under — when the walk stopped on that
 * clause: not executed, and no turn consumed before the stop (D91's line).
 * A question a later clause of the player's chain asked is that clause's
 * own, and carries nothing.
 *
 * @param state - The state before the input
 * @param ruledOutRefs - What the answered clause had settled (absent when the input was no answer)
 * @param outcome - The input's outcome
 * @returns The settlement to carry, or `undefined`
 */
const settlementAskedAbout = (state: RuntimeState, ruledOutRefs: RuledOutRefs, outcome: PlayerInputOutcome): RuledOutRefs =>
	outcome.type !== 'executed' && outcome.nextState.progression.turnCount === state.progression.turnCount ? ruledOutRefs : undefined;

/**
 * The conversation over a pair of texts (D226, E12): what the player TYPED,
 * which the echo and the history keep, and the WORDS the conversation reads
 * — the same text, except under `again`, where the words are an earlier
 * input's. Every reading below — the withdrawal, the chrome, a which-one
 * answer, a completion's answer, the parse — reads the words; every record
 * keeps the typed input. D37 drew this line for the splice (typed vs
 * parsed); `again` is the same line, drawn one layer up, so that a repeated
 * `kettle` still answers the question it was first an answer to.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed
 * @param words - The words being read as this input
 * @returns One §3.3 outcome, next state committed on every arm
 */
const processPlayerWords = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string, words: string): PlayerInputOutcome => {
	// R34 (D114): `never mind` — the question's own escape hatch, checked
	// before EITHER question kind reads the input as an answer, because a
	// withdrawal outranks answering: under a verb-slot question a bare verb
	// answers (§3.4), and this is the one verb that must not. The chip's
	// dismiss submits these same words, so replays carry the gesture. The
	// R11 terminal gate still outranks IT (checked inside the predicate) —
	// a closed world's inputs fall through to the ordinary pipeline, whose
	// gate declines them after the echo commit.
	if (isWithdrawalDirective(bundle, state, words)) {
		return withdrawParkedQuestion(bundle, state, rawInput);
	}

	// D179: a parked confirm's answer, or a chrome directive — read before
	// either question kind, and before the parser. A confirm that was not
	// answered closes silently and the input is read again as itself. D226:
	// `again` re-enters HERE with an earlier input's words, so the repeated
	// words meet every gate the originals met.
	const chrome = readChrome(
		bundle,
		state,
		rawInput,
		words,
		(cleared) => processPlayerWords(bundle, cleared, rawInput, words),
		(cleared, parseText) => runPlayerInput(bundle, cleared, rawInput, parseText),
		(repeatedWords) => processPlayerWords(bundle, state, rawInput, repeatedWords),
	);

	if (chrome !== undefined) {
		return chrome;
	}

	const parkedQuestion = state.session.pendingQuestion;

	// An ACTOR's question (D235) or a which-one question answered by name
	// resolves before the parser reads anything — typed words and chip clicks
	// are ONE path, so the transcript records the answer as the player's own.
	const answeredByName = answerParkedQuestionByName(bundle, state, rawInput, words, parkedQuestion);

	if (answeredByName !== undefined) {
		return answeredByName;
	}

	const completion = parkedQuestion?.kind === 'completion' ? parkedQuestion : undefined;
	const attempt = readAnswerAttempt(bundle, state, completion, words);
	const { isAnswer, parseText } = attempt;
	const outcome = runPlayerInput(
		bundle,
		clearQuestion(state),
		rawInput,
		parseText,
		attempt.answerRefusal,
		attempt.clauseCatalog,
		attempt.answerRejectionOverride,
	);

	// D41: "To search what?" is a QUESTION too — park the verb so a bare noun
	// finishes it, exactly as the refusal-shaped questions do.
	if (outcome.type === 'parse_failed') {
		const objectQuestion = questionForMissingObject(outcome, words);

		if (objectQuestion !== undefined) {
			return {
				...outcome,
				nextState: parkQuestion(outcome.nextState, objectQuestion),
			};
		}

		// D212: "Did you mean TAKE THE DRUM?" is a question too — a confirm,
		// answered by its chips or by yes/no, closed by anything else. Only a
		// PARKED question returns here; every other miss goes on to the
		// answer-restoring rules below like any failed input.
		const asked = withCorrectionQuestion(bundle, outcome);

		if (asked !== outcome) {
			return asked;
		}
	}

	/*
	 * D91 (Calder's R21 verification, §1.2): the ANSWER's fate is its clause's,
	 * never the whole input's. The interleave made "the answer succeeded and a
	 * LATER clause stopped the walk" reachable for the first time — and the
	 * restore paths, keyed on the OUTCOME's arm, brought back a question the
	 * player had already answered (which then re-spliced on their next bare
	 * noun: a ghost asking to disambiguate a locker two turns after the fact).
	 * A refusal or a teach consumes no turn, so a turn consumed before the
	 * stop is proof the answer EXECUTED — the question did its job, and the
	 * stop belongs to the chain the player appended.
	 */
	const didAnswerExecuteBeforeStop =
		isAnswer &&
		outcome.type !== 'executed' &&
		outcome.nextState.progression.turnCount > state.progression.turnCount;

	/*
	 * R33 (D113; Calder's §2, superseding D66's re-park): **a RESOLVED answer
	 * closes the question, whatever the outcome.** The engine found the thing,
	 * tried the pair, and reported that they do not work — the exchange
	 * COMPLETED, and that is exactly the distinction the two answer slots
	 * already encode (`answerDoesNotServe` = resolved and refused, closes;
	 * `answerNotUnderstood` = never resolved, keeps the question standing via
	 * the survival rules below). Re-parking after a resolved refusal was a
	 * courtesy that cost a trap: the question outlived resolved answers,
	 * executed commands, and gibberish, and every later unrecognized input
	 * for the rest of the session was answered in its terms. The one case
	 * that still re-asks: the refusal itself earning a NEW question ("locker"
	 * → "what would you like done with it?" → "open" → "Open it with what?")
	 * — the new question is the conversation.
	 */
	const settlement = settlementAskedAbout(state, attempt.ruledOutRefs, outcome);

	if (completion !== undefined && isAnswer && outcome.type === 'action_rejected' && !didAnswerExecuteBeforeStop) {
		return carryingSettlement(settleRefusedAnswer(bundle, outcome), settlement);
	}

	// A SUCCESSFUL answer closes its question (it did its job) — whether the
	// input then ended executed, or a LATER clause of it was refused or failed
	// to parse (D91); everything else falls through to the survival rules with
	// the question intact.
	const wasAnswered = (isAnswer && outcome.type === 'executed') || didAnswerExecuteBeforeStop;

	// D37's bare-noun offer now rides inside `runPlayerInput` (D133): the
	// parser marks a naming clause, and the executed arm parks the follow-up.
	const askedOutcome = withPendingCompletion(bundle, outcome);

	// D79: everything after the ladder — the clarification park, survival,
	// replacement, and the room-leave withdrawal — is the question model's own
	// behavior, and lives with it.
	// D245: a command of the player's own under a completion is the player moving on — the question lets go.
	return carryingSettlement(
		applyQuestionSurvival(bundle, parkedQuestion, wasAnswered ? 'answer' : attempt.movesOn ? 'moved_on' : 'neither', askedOutcome),
		settlement,
	);
};
